import { useState } from 'react'
import { Loader2, Square } from 'lucide-react'
import { toast } from 'sonner'

import { WorkflowExecutionStatus } from '@/gen/cortado/v1/cortado_pb'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { getErrorMessage } from '@/lib/error-reporting'
import { useCancelWorkflowExecution } from '@/stores/workflows.store'

/**
 * An execution is stoppable only while it is still in flight. Cancelling a
 * terminal run is rejected by the backend (FailedPrecondition).
 */
function isActiveStatus(status: WorkflowExecutionStatus): boolean {
  return (
    status === WorkflowExecutionStatus.RUNNING ||
    status === WorkflowExecutionStatus.PENDING
  )
}

interface StopWorkflowRunButtonProps {
  executionId: string
  status: WorkflowExecutionStatus
  /** Visual size of the trigger button. Defaults to 'sm'. */
  size?: 'sm' | 'default' | 'icon'
}

/**
 * Destructive "Stop" control for an in-flight workflow run. Renders nothing for
 * terminal runs, opens a confirm dialog, calls CancelWorkflowExecution, and
 * surfaces success/error toasts. Reusable by the per-entity tab, the global
 * control tower, and the per-definition Runs table.
 */
export function StopWorkflowRunButton({
  executionId,
  status,
  size = 'sm',
}: StopWorkflowRunButtonProps) {
  const [open, setOpen] = useState(false)
  const { cancelExecution, isPending } = useCancelWorkflowExecution()

  if (!isActiveStatus(status)) return null

  const handleConfirm = async () => {
    try {
      await cancelExecution(executionId)
      toast.success('Run stopped')
      setOpen(false)
    } catch (error) {
      const copy = getErrorMessage(error)
      toast.error('Failed to stop run', {
        description:
          error instanceof Error ? error.message : copy.description,
      })
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button
        variant="destructive"
        size={size}
        disabled={isPending}
        onClick={() => setOpen(true)}
      >
        {isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Square className="size-4" />
        )}
        Stop
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Stop this workflow run?</AlertDialogTitle>
          <AlertDialogDescription>
            It can&apos;t be resumed. Any remaining steps for this run will not
            execute.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isPending}
            onClick={(event) => {
              // Prevent the dialog from auto-closing before the async cancel
              // resolves; we close it ourselves on success.
              event.preventDefault()
              void handleConfirm()
            }}
          >
            {isPending && <Loader2 className="size-4 animate-spin" />}
            Stop run
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
